import React, { useState, useEffect } from 'react';
import { Navbar, Nav, NavDropdown, Container, Offcanvas } from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { useLocation } from 'react-router-dom';
import './navbar.scss';

import LanguageSwitcher from '../03_language/language';
import logo from '../../01_ASSETS/02_media/logo/LapponiaSilent_logo.png';

const NavigationBar = () => {
    const { t } = useTranslation();
    const { footerColor } = useTheme();
    const location = useLocation();
    const pathsInDropdown = ["/hunting", "/fishing", "/nature"];
    const [currentPath, setCurrentPath] = useState(location.pathname); 

    const [showOffcanvas, setShowOffcanvas] = useState(false);
    const closeOffcanvas = () => setShowOffcanvas(false);

    const handleLinkClick = (path) => {
        setCurrentPath(path);
        closeOffcanvas();
    }

    // Update currentPath when location changes
    useEffect(() => {
        setCurrentPath(location.pathname);
    }, [location.pathname]);

    const [prevScrollPos, setPrevScrollPos] = useState(window.pageYOffset);
    const [isVisible, setIsVisible] = useState(true);

    useEffect(() => {
        let ticking = false;
        const scrollThreshold = 10; // Minimum scroll distance to trigger hide/show
        const topThreshold = 100; // Always show navbar when within this distance from top

        const handleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    const currentScrollPos = window.pageYOffset;
                    const scrollDifference = Math.abs(currentScrollPos - prevScrollPos);

                    // Always show navbar at top of page
                    if (currentScrollPos < topThreshold) {
                        setIsVisible(true);
                    }
                    // Only hide/show if scrolled more than threshold
                    else if (scrollDifference > scrollThreshold) {
                        const visible = prevScrollPos > currentScrollPos;
                        setIsVisible(visible);
                        setPrevScrollPos(currentScrollPos);
                    }

                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener("scroll", handleScroll, { passive: true });

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, [prevScrollPos]);

    useEffect(() => {
        const content = document.querySelector('.lapponia-silent');
        if (showOffcanvas) {
            content.classList.add('blur');
        } else {
            content.classList.remove('blur');
        }
    }, [showOffcanvas]);

    return (
        <Navbar
            expand="md"
            fixed="top"
            variant="dark"
            style={{
                transform: isVisible ? 'translateY(0)' : 'translateY(-100%)',
                transition: 'transform 0.3s ease-in-out'
            }}
        >
            <LinkContainer to="/" onClick={() => handleLinkClick('/')}>
                <Navbar.Brand className='brandname'>
                    <div
                        className='navbar-logo-container'
                        style={{
                            '--glow-primary': footerColor.primary,
                            '--glow-secondary': footerColor.secondary
                        }}
                    >
                        <img src={logo} alt="Lapponia Silent" className='navbar-logo' />
                    </div>
                    Lapponia Silent
                </Navbar.Brand>
            </LinkContainer>

            <Navbar.Toggle aria-controls="offcanvasNavbar" onClick={() => setShowOffcanvas(!showOffcanvas)}/>

            <Navbar.Offcanvas
                id="offcanvasNavbar"
                aria-labelledby="offcanvasNavbarLabel"
                placement="end"
                show={showOffcanvas}
                onHide={closeOffcanvas}
            >
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title id="offcanvasNavbarLabel">{t('navbar.menu')}</Offcanvas.Title>
                </Offcanvas.Header>

                <Offcanvas.Body>
                    <Nav className="justify-content-end flex-grow-1 pe-1">
                        <LinkContainer to="/" onClick={() => handleLinkClick('/')}>
                            <Nav.Link>{t('navbar.home')}</Nav.Link>
                        </LinkContainer>

                        <NavDropdown
                            title={t('navbar.activities')}
                            className={pathsInDropdown.includes(currentPath) ? 'dropdown-active' : 'dropdown'}
                        >
                            <LinkContainer to="/hunting" onClick={() => handleLinkClick('/hunting')} className={currentPath === "/hunting" ? 'dropdown-item-active' : 'dropdown-item'}>
                                <NavDropdown.Item>{t('navbar.hunting')}</NavDropdown.Item>
                            </LinkContainer>
                            
                            <LinkContainer to="/fishing" onClick={() => handleLinkClick('/fishing')} className={currentPath === "/fishing" ? 'dropdown-item-active' : 'dropdown-item'}>
                                <NavDropdown.Item>{t('navbar.fishing')}</NavDropdown.Item>
                            </LinkContainer>
                            
                            <LinkContainer to="/nature" onClick={() => handleLinkClick('/nature')} className={currentPath === "/nature" ? 'dropdown-item-active' : 'dropdown-item'}>
                                <NavDropdown.Item>{t('navbar.nature')}</NavDropdown.Item>
                            </LinkContainer>
                        </NavDropdown>

                        <LinkContainer to="/about" onClick={() => handleLinkClick('/about')}>
                            <Nav.Link>{t('navbar.about')}</Nav.Link>
                        </LinkContainer>

                        <LinkContainer to="/faq" onClick={() => handleLinkClick('/faq')}>
                            <Nav.Link>{t('navbar.faq')}</Nav.Link>
                        </LinkContainer>

                        <LinkContainer to="/contact" className="last-link" onClick={() => handleLinkClick('/contact')}>
                            <Nav.Link>{t('navbar.contact')}</Nav.Link>
                        </LinkContainer>
                    </Nav>
                    
                    <Container className='navbar-language'>
                        <LanguageSwitcher />
                        <div className='languages'>
                            <p className='selected-language'>{t('navbar.selected-language')}</p>
                            <p className='language'>{t('navbar.language')}</p>
                        </div>
                    </Container>

                </Offcanvas.Body>
            </Navbar.Offcanvas>
        </Navbar>
    );
};

export default NavigationBar;
